import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormLabel,
    Input,
    List,
    ListItem,
    ListItemDecorator,
    Modal,
    ModalClose,
    ModalDialog,
    Typography
} from "@mui/joy";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Trash2, X} from "lucide-react";
import {User} from "../models/user";

type DeleteAccountDialogProps = {
    open?: boolean
    user?: User
    loading?: boolean
    onClose?: () => void
    onConfirm?: (confirmation: { password?: string, confirmation?: string }) => void
}

/**
 * Confirms a permanent account deletion. Password accounts re-enter their password; Google and
 * Facebook accounts have none, so they type their email address instead.
 */
const DeleteAccountDialog = (props: DeleteAccountDialogProps) => {
    const {t} = useTranslation()
    const [value, setValue] = useState("")
    const providerAccount = !!props.user?.authProviderId && props.user.authProviderId !== "password"
    const canConfirm = providerAccount ?
        value.trim().toLowerCase() === props.user?.email?.toLowerCase() :
        value.length > 0

    useEffect(() => {
        if (!props.open)
            setValue("")
    }, [props.open])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (canConfirm)
            props.onConfirm?.(providerAccount ? {confirmation: value.trim()} : {password: value})
    }

    return <Modal open={!!props.open} onClose={props.loading ? undefined : props.onClose}>
        <ModalDialog variant="outlined" role="alertdialog" size={"lg"}
                     sx={{width: {xs: "100%", sm: "32rem"}, maxWidth: "calc(100vw - 2rem)", boxSizing: "border-box"}}>
            <ModalClose disabled={props.loading}/>
            <DialogTitle>
                {t("deleteAccountDialogTitle")}
            </DialogTitle>
            <Divider/>
            <DialogContent sx={{gap: 1.5}}>
                <Typography level={"body-md"}>
                    {t("deleteAccountDialogDescription")}
                </Typography>

                <List size={"sm"} sx={{"--ListItem-paddingX": 0}}>
                    {["deleteAccountDialogItemProfile", "deleteAccountDialogItemResults",
                        "deleteAccountDialogItemRecordings", "deleteAccountDialogItemSubscription"].map(key =>
                        <ListItem key={key}>
                            <ListItemDecorator sx={{color: "danger.500"}}><X size={16}/></ListItemDecorator>
                            {t(key)}
                        </ListItem>)}
                </List>

                <Typography level={"body-sm"} textColor={"text.tertiary"}>
                    {t("deleteAccountDialogPaymentNotice")}
                </Typography>

                <Box component={"form"} id={"delete-account-form"} onSubmit={handleSubmit} paddingTop={1}>
                    <FormControl required>
                        <FormLabel>
                            {providerAccount ?
                                t("deleteAccountEmailPrompt", {email: props.user?.email}) :
                                t("deleteAccountPasswordPrompt")}
                        </FormLabel>
                        <Input type={providerAccount ? "email" : "password"}
                               autoComplete={providerAccount ? "off" : "current-password"}
                               value={value}
                               onChange={e => setValue(e.target.value)}
                               placeholder={providerAccount ? props.user?.email : t("currentPasswordFieldPlaceholder")}/>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button form={"delete-account-form"} type={"submit"} variant="solid" color="danger"
                        startDecorator={<Trash2 size={18}/>}
                        loading={props.loading} disabled={!canConfirm}>
                    {t("deleteAccountConfirmActionLabel")}
                </Button>
                <Button variant="plain" color="neutral" onClick={props.onClose} disabled={props.loading}>
                    {t("cancelDialogActionButtonLabel")}
                </Button>
            </DialogActions>
        </ModalDialog>
    </Modal>
}

export default DeleteAccountDialog
